'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const challenges = [
  {
    tag: '[01]',
    title: 'Critical National Infrastructure Obligations',
    desc: 'Telecoms operators are designated CNI. NIS2, the Telecoms Security Act, and Ofcom codes of practice impose strict security duties, with significant penalties for non-compliance.',
  },
  {
    tag: '[02]',
    title: 'Massive Attack Surface',
    desc: 'From core network infrastructure and OSS/BSS platforms to customer-facing APIs and IoT endpoints, the telecom attack surface spans millions of devices and dozens of interconnected systems.',
  },
  {
    tag: '[03]',
    title: 'Legacy Network & Protocol Risk',
    desc: 'SS7, Diameter, and legacy signalling protocols carry known vulnerabilities that are difficult to remediate without disrupting live services. Modern 5G introduces new threat vectors alongside old ones.',
  },
  {
    tag: '[04]',
    title: 'Supply Chain & Vendor Dependency',
    desc: 'Telecom networks depend on a small number of equipment vendors. Supply chain compromise, firmware integrity, and third-party ICT risk management are board-level concerns.',
  },
];

const services = [
  { label: 'Telecoms Security Act Compliance', href: '/services/compliance', desc: 'Mapping your security controls to the TSA and Ofcom codes of practice. Continuous compliance, not point-in-time assessments.' },
  { label: 'NIS2 & CNI Security Programme', href: '/services/security-programmes', desc: 'Bespoke security programmes aligned to NIS2 obligations for essential entities, built around your network, not a generic framework.' },
  { label: 'Threat Modelling for Telecom Networks', href: '/services/threat-modelling', desc: 'STRIDE and MITRE ATT&CK applied to core network, signalling layers, and customer data flows. Architecture-level risk identification before incidents occur.' },
  { label: 'IaC & Pipeline Security', href: '/services/iac-pipeline', desc: 'Infrastructure-as-code security for cloud-native network functions, containerised workloads, and CI/CD pipelines deploying to production network environments.' },
  { label: 'Penetration Testing', href: '/services/pentesting', desc: 'Network, API, and application penetration testing across OSS/BSS platforms, customer portals, and internal tooling, with telecom-specific threat context.' },
  { label: 'Security Focus Programme', href: '/services/security-focus', desc: 'Cut through the noise of overlapping regulatory obligations. An auditor-aligned programme that focuses effort on controls that close real exposure.' },
];

const outcomes = [
  { metric: 'TSA', label: 'Telecoms Security Act obligations mapped and evidenced' },
  { metric: 'NIS2', label: 'Essential entity requirements met across network and supply chain' },
  { metric: 'Ofcom', label: 'Codes of practice aligned with continuous compliance monitoring' },
  { metric: '5G', label: 'Security architecture reviewed for new network deployments' },
];

export default function TelecomPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest">Industries</span>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Telecom</span>
          </div>
          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Telecom]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Security for networks<br />
              <span className="text-[#80edfa]">that cannot go down.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              Telecoms operators face overlapping CNI obligations, legacy protocol risk, and a threat landscape that includes nation-state actors. TechSonar embeds senior security engineers to build continuous compliance, harden network infrastructure, and satisfy Ofcom and NIS2 requirements, without disrupting live services.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] py-12 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {outcomes?.map((o, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-2xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-xs text-[#555] leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">The challenge</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              What makes telecom security hard.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {challenges?.map((c, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{c?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{c?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{c?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">How we help</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Services tailored to telecoms operators.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {services?.map((s, i) => (
              <Link
                key={i}
                href={s?.href}
                className="group bg-[#0a0f14] p-8 hover:bg-[#111519] transition-colors duration-300 fade-in-up"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-lg text-[#f5f5f8] font-medium group-hover:text-[#80edfa] transition-colors pr-4">{s?.label}</h3>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors flex-shrink-0 mt-1" viewBox="0 0 16 16" fill="none">
                    <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-sm text-[#555] leading-relaxed group-hover:text-[#888] transition-colors">{s?.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
              Ready to meet your CNI security obligations?
            </h2>
            <p className="text-[#aaa] text-lg leading-relaxed mb-8">
              Whether you are preparing for an Ofcom review, mapping to NIS2, or hardening your 5G network architecture, TechSonar can get you there. Senior engineers, embedded, doing the work.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services/security-programmes" className="px-6 py-3 border border-[#333] text-[#aaa] text-sm uppercase tracking-wide hover:border-[#80edfa] hover:text-[#80edfa] transition-colors font-sans">
                View Security Programmes
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
